import { useCallback, useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { createCredentials } from "@/redux/ducks/customer";
import { validatePassword } from "@/utils/functions";
import useCybersourceService from "@/hooks/useCybersourceService";

const pinScheme = z.object({
  password: z.string().refine((val) => validatePassword(val)),
  confirmPassword: z.string().refine((val) => validatePassword(val)),
});

type State = {
  modalOpen: boolean;
  loading: boolean;
  error: boolean;
  errorPlate: boolean;
  success: boolean;
};

export default function useAccessForm() {
  const dispatch = useDispatch();

  const { addCyberSourceScripts, getSessionId } = useCybersourceService();

  const [ui, setUi] = useState<State>({
    modalOpen: false,
    loading: false,
    error: false,
    errorPlate: false,
    success: false,
  });

  const unifiedSchema = pinScheme;
  type FormData = z.infer<typeof unifiedSchema>;

  const defaultValues: FormData = {
    password: "",
    confirmPassword: "",
  };

  const {
    register,
    handleSubmit,
    formState: { errors, isValid },
    watch,
    reset,
  } = useForm<FormData>({
    resolver: zodResolver(unifiedSchema),
    mode: "onChange",
    defaultValues,
  });

  const password = watch("password");
  const confirmPassword = watch("confirmPassword");

  const toggleModal = useCallback(() => {
    setUi((prev) => ({ ...prev, modalOpen: !prev.modalOpen }));
  }, []);

  const handleCreateCredentials = useCallback(
    (success: boolean, errorMessage?: string) => {
      setUi((prev) => ({ ...prev, loading: false }));

      if (!success) {
        if (errorMessage === "already") {
          setUi((prev) => ({ ...prev, errorPlate: true }));
        }
        if (errorMessage === "timeout") {
          setUi((prev) => ({ ...prev, error: true }));
        }
        toggleModal();
        return;
      }

      setUi((prev) => ({ ...prev, success: true }));
      reset();
    },
    [reset, toggleModal],
  );

  const onSubmit = useCallback(
    (data: FormData) => {
      if (ui.loading) return;
      setUi((prev) => ({ ...prev, loading: true }));

      const visitorId = getSessionId();

      dispatch(
        createCredentials({
          visitorId: visitorId!,
          password: data.password,
          callback: handleCreateCredentials,
        }),
      );
    },
    [ui.loading, dispatch, handleCreateCredentials, getSessionId],
  );

  useEffect(() => {
    addCyberSourceScripts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return {
    toggleModal,
    isVisibleModal: ui.modalOpen,
    isLoading: ui.loading,
    isDisabled: !isValid || ui.loading,
    register,
    handleSubmit: handleSubmit(onSubmit),
    errors,
    error: ui.error,
    errorPlate: ui.errorPlate,
    success: ui.success,
    password,
    confirmPassword,
  };
}
